import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchCompanyLocations,
  fetchActiveLocations,
  getDefaultLocation,
} from '../locationRepository';
import { ensureDefaultLocationForCompany } from '../migrateDefaultLocation';
import { shouldShowLocationStep } from '../filterByLocation';

const migrationPromises = new Map();

const runMigrationOnce = (companyId, companyData) => {
  if (!companyId) return Promise.resolve(null);

  if (migrationPromises.has(companyId)) {
    return migrationPromises.get(companyId);
  }

  const promise = ensureDefaultLocationForCompany(companyId, companyData)
    .finally(() => {
      migrationPromises.delete(companyId);
    });

  migrationPromises.set(companyId, promise);
  return promise;
};

export const useCompanyLocations = ({
  companyId,
  companyData = null,
  activeOnly = false,
  autoMigrate = true,
} = {}) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(Boolean(companyId));
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const reload = useCallback(async () => {
    if (!companyId) {
      setLocations([]);
      setLoading(false);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      if (autoMigrate) {
        await runMigrationOnce(companyId, companyData);
      }

      const nextLocations = activeOnly
        ? await fetchActiveLocations(companyId)
        : await fetchCompanyLocations(companyId);

      if (mountedRef.current) {
        setLocations(nextLocations);
      }

      return nextLocations;
    } catch (loadError) {
      console.error('[useCompanyLocations] failed to load locations', loadError);
      if (mountedRef.current) {
        setError(loadError);
        setLocations([]);
      }
      return [];
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [companyId, companyData, activeOnly, autoMigrate]);

  useEffect(() => {
    mountedRef.current = true;
    reload();
    return () => {
      mountedRef.current = false;
    };
  }, [reload]);

  const defaultLocation = useMemo(
    () => getDefaultLocation(locations),
    [locations]
  );

  const activeLocations = useMemo(
    () => locations.filter((location) => location.active !== false),
    [locations]
  );

  const showLocationStep = useMemo(
    () => shouldShowLocationStep(locations),
    [locations]
  );

  return {
    locations,
    activeLocations,
    defaultLocation,
    showLocationStep,
    loading,
    error,
    reload,
  };
};

export default useCompanyLocations;
